/**
 * Settings Context
 *
 * Manages user preferences and settings, stored in localStorage.
 * Preferred date format and timezone normalise date display across the app and reports.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import type { PreferredDateFormat } from '@/utils/dateFormat'

export type DateFormat = PreferredDateFormat

function getDefaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC'
  } catch {
    return 'UTC'
  }
}

interface Settings {
  dateFormat: PreferredDateFormat
  timezone: string
  /** When false, times are shown in 12-hour format; when true, 24-hour. */
  use24Hour: boolean
  /** Default Top N for ranked analytics charts (25, 50, 100, 200). */
  analyticsDefaultTopN: number
  /** Show data labels on chart by default in focus view. */
  analyticsShowLabels: boolean
}

interface SettingsContextType {
  settings: Settings
  updateDateFormat: (format: PreferredDateFormat) => void
  updateTimezone: (timezone: string) => void
  updateUse24Hour: (use24Hour: boolean) => void
  updateAnalyticsDefaultTopN: (value: number) => void
  updateAnalyticsShowLabels: (value: boolean) => void
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined)

const DEFAULT_SETTINGS: Settings = {
  dateFormat: 'DD/MM/YYYY',
  timezone: getDefaultTimezone(),
  use24Hour: false, // default to 12-hour time
  analyticsDefaultTopN: 50,
  analyticsShowLabels: true,
}

const STORAGE_KEY = 'user_settings'

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        return {
          dateFormat: (parsed.dateFormat || DEFAULT_SETTINGS.dateFormat) as PreferredDateFormat,
          timezone: typeof parsed.timezone === 'string' && parsed.timezone ? parsed.timezone : getDefaultTimezone(),
          use24Hour: typeof parsed.use24Hour === 'boolean' ? parsed.use24Hour : DEFAULT_SETTINGS.use24Hour,
          analyticsDefaultTopN: typeof parsed.analyticsDefaultTopN === 'number' && [25, 50, 100, 200].includes(parsed.analyticsDefaultTopN) ? parsed.analyticsDefaultTopN : DEFAULT_SETTINGS.analyticsDefaultTopN,
          analyticsShowLabels: typeof parsed.analyticsShowLabels === 'boolean' ? parsed.analyticsShowLabels : DEFAULT_SETTINGS.analyticsShowLabels,
        }
      }
    } catch (error) {
      console.error('Error loading settings from localStorage:', error)
    }
    return DEFAULT_SETTINGS
  })

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
    } catch (error) {
      console.error('Error saving settings to localStorage:', error)
    }
  }, [settings])

  const updateDateFormat = (format: PreferredDateFormat) => {
    setSettings(prev => ({ ...prev, dateFormat: format }))
  }

  const updateTimezone = (timezone: string) => {
    setSettings(prev => ({ ...prev, timezone }))
  }

  const updateUse24Hour = (use24Hour: boolean) => {
    setSettings(prev => ({ ...prev, use24Hour }))
  }

  const updateAnalyticsDefaultTopN = (value: number) => {
    setSettings(prev => ({ ...prev, analyticsDefaultTopN: value }))
  }

  const updateAnalyticsShowLabels = (value: boolean) => {
    setSettings(prev => ({ ...prev, analyticsShowLabels: value }))
  }

  return (
    <SettingsContext.Provider value={{ settings, updateDateFormat, updateTimezone, updateUse24Hour, updateAnalyticsDefaultTopN, updateAnalyticsShowLabels }}>
      {children}
    </SettingsContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- useSettings is hook, SettingsProvider is component
export function useSettings() {
  const context = useContext(SettingsContext)
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return context
}


